import { API, graphqlOperation } from "aws-amplify";
import * as mutations from "@/graphql/mutations";
import { CreateHistoryInput, CreateSelesSenderInput } from "@/API";
import { FileData, FileGroupData, FileSearchService, SearchCondition } from "@/services/files/FileSearch"
import { getSetting } from "@/utils/AccountSettingUtils";
import store from "@/store";
import { decodeRoleJson, UserRole } from "@/utils/RoleUtils";
import { getAccountId, getDepartment } from "@/utils/AuthUtils";
import { fileSaleSubscriptionService } from "@/services/files/FileSaleSubscription";
import { uuid } from "vue-uuid";

// エラータイプ定数
const ERROR_TYPE = {
    VALIDATION_ERROR: 'validation_error',        // バリデーションエラー（通知なし）
    PROCESSING_CONFLICT: 'processing_conflict',  // 処理中の重複リクエスト（通知なし）
    PROCESSING_ERROR: 'processing_error'         // 処理中のエラー（通知あり）
};

// カスタムエラークラス
export class SaleSendError extends Error {
    errorType: string;
    fileId: string;
    requestId?: string;
    
    constructor(errorType: string, message: string, fileId: string, requestId?: string) {
        super(message);
        this.errorType = errorType;
        this.fileId = fileId;
        this.requestId = requestId;
        this.name = 'SaleSendError';
    }
}

/**
 * 一意のリクエストIDを生成（UUID v4）
 * 
 * @returns UUID v4形式の文字列（36文字）
 */
export function generateRequestId(): string {
    return uuid.v4();
}

/**
 * Lambda側から返されたエラーのerror_typeをチェックして処理を判定
 * AppSyncのエラー構造: { data: { createSelesSender: null }, errors: [{ message: "{...JSON...}" }] }
 */
function parseErrorType(error: unknown): { errorType: string | null; message: string; fileId: string } | null {
    if (!error) return null;
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err = error as any;
    
    // AppSyncのエラー構造をチェック (最優先)
    // errors[].messageにJSON文字列が入っている
    if (err.errors && Array.isArray(err.errors)) {
        for (const e of err.errors) {
            try {
                const messageStr = e.message || '';
                if (typeof messageStr === 'string' && messageStr.trim().startsWith('{')) {
                    const parsed = JSON.parse(messageStr);
                    if (parsed.error_type && parsed.file_id) {
                        return {
                            errorType: parsed.error_type,
                            message: parsed.message || '',
                            fileId: parsed.file_id
                        };
                    }
                }
            } catch (parseError) {
                // JSONパースエラーは無視して次へ
                console.debug('Failed to parse error message as JSON:', e.message);
            }
        }
    }
    
    // 直接error.messageがJSON文字列の場合もチェック
    try {
        if (err.message && typeof err.message === 'string' && err.message.trim().startsWith('{')) {
            const parsed = JSON.parse(err.message);
            if (parsed.error_type && parsed.file_id) {
                return {
                    errorType: parsed.error_type,
                    message: parsed.message || '',
                    fileId: parsed.file_id
                };
            }
        }
    } catch (e) {
        // JSONパースエラーは無視
    }
    
    return null;
}

/**
 * 指定されたrequestIdで外販送信を実行
 * 
 * @param file - 送信するファイル
 * @param requestId - 一意のリクエストID（事前に生成済み）
 * @returns 使用したrequestId
 */
export async function sendSaleFile(file: FileData, requestId: string): Promise<string> {
    let accountId = "";
    let userId = "";
    let userName = "";
    const user = store.getters.user;

    if (user) {
        accountId = user.attributes["custom:account_id"];
        userId = user.attributes.sub;
        userName = user.attributes.name;
    }

    const fileId = file.id ?  file.id : "";

    try {
        // データチェックはLMBDA内部で実装
        // accountIdとuserIdはサーバー側でevent.identity.claimsから取得
        const input: CreateSelesSenderInput = {
            requestId: requestId,
            fileId: fileId
        };
        await API.graphql(graphqlOperation(mutations.createSelesSender, { input: input }));

        const saleSendHistry: CreateHistoryInput = {
            data: JSON.stringify({ 'tableName': 'File', 'id': fileId, 'requestId': requestId }),
            body: `外販送信されました。 外販送信ユーザー名:[${userName}] 外販送信ファイルID:[${fileId}] リクエストID:[${requestId}]`,
            operation: "sale send",
            userId: userId,
            accountId: accountId
        };
        await API.graphql(graphqlOperation(mutations.createHistory, { input: saleSendHistry }));
        
        return requestId;
    } catch (error) {
        // Lambda側から返されたエラーのerror_typeに応じて処理を分岐
        const errorInfo = parseErrorType(error);
        
        if (errorInfo) {
            const { errorType, message, fileId: errorFileId } = errorInfo;
            
            switch (errorType) {
                case ERROR_TYPE.VALIDATION_ERROR:
                    // バリデーションエラー: Subscriptionを完全に解除（通知なし）
                    fileSaleSubscriptionService.unsubscribe(requestId);
                    throw new SaleSendError(errorType, message, errorFileId, requestId);
                    
                case ERROR_TYPE.PROCESSING_CONFLICT:
                    // 処理中の重複リクエスト: Subscriptionを完全に解除（通知なし）
                    fileSaleSubscriptionService.unsubscribe(requestId);
                    throw new SaleSendError(errorType, message, errorFileId, requestId);
                    
                case ERROR_TYPE.PROCESSING_ERROR:
                    // 処理中のエラー: Subscriptionは停止するがコールバックは保持（通知あり）
                    // 何もしない（subscriptionはエラーハンドラで停止済み、コールバックは保持）
                    throw new SaleSendError(errorType, message, errorFileId, requestId);
                    
                default:
                    // 不明なエラータイプの場合もunsubscribe
                    fileSaleSubscriptionService.unsubscribe(requestId);
                    throw error;
            }
        }
        
        // エラー情報が取得できない場合（GraphQLバリデーションエラーなど）
        // createSelesSender自体が失敗しているため、subscriptionを解除
        fileSaleSubscriptionService.unsubscribe(requestId);
        
        // エラーを再スロー
        throw error;
    }
}

export async function selesSendFiles(files: Array<FileData>) {
    for (const file of files) {
        // requestIdを生成してから送信
        const requestId = generateRequestId();
        await sendSaleFile(file, requestId);
    }
}

export async function isAllowSendFile(file: FileData) {
    const item = file?.customItems || "{}"
    const fileSetting = JSON.parse(item);
    const accountSetting = await getSetting();
    const allowTypes = accountSetting?.allowTypes || [];
    if((fileSetting?.sale === 1 || fileSetting?.sale === 2) && allowTypes.findIndex(x => x === file.type)>=0){
        return true;
    }
    return false;
}

export async function isAllowSendFileOperate(file: FileData): Promise<boolean> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const user: any = store.state.user;
  if (user === null) return false;
  const userRole: UserRole = decodeRoleJson(user.attributes["custom:role"]);
  const userId = user.username;
  const department = getDepartment(user);
  const accountID = getAccountId(user);
  const role = userRole["sale"];
  if (!role) return false;
  const operation = role["operate"];

  if (operation === "SELF" && file.createdUserId === userId) {
    return true;
  }
  if (operation === "DEPARTMENT" && file.ownedDepartment === department) {
    return true;
  }
  if (operation === "COMPANY" && file.ownedAccountId === accountID) {
    return true;
  }
  return false;
}

export async function selesSendFileGroup(fileGroup: FileGroupData) {
    if(fileGroup && fileGroup.id){
        const fileSearchService = new FileSearchService();
        const condition: SearchCondition = {
            groupIds: [fileGroup.id],
        }
        const files = await fileSearchService.searchFileDataFullList(condition);
        for (const key in files) {
            if (Object.prototype.hasOwnProperty.call(files, key)) {
                isAllowSendFile(await files[key]);
            }
        }
    }
}

export async function selesSendGroups(fileGroups: Array<FileGroupData>) {
    for (const fileGroup of fileGroups) {
        await selesSendFileGroup(fileGroup);
    }
}